/**
 * One item, opened from the table at `?item=<item_result_id>`. The outcome
 * comes first: every evaluator's score with its reason and, when the API sends
 * them, the values its slots read (no Scores section when the experiment has
 * no evaluators). Below it: its inputs as labelled fields, what was expected,
 * what came out, the trace (tool calls, retrieved context) and the run. The
 * URL holds it, so a pasted link opens the same item.
 */

import { DetailSection as Section } from "@/components/ui/box";
import { JsonView } from "@/components/ui/json-view";
import { SidePanel } from "@/components/ui/overlay";
import { Pill } from "@/components/ui/pill";
import { QueryState } from "@/components/ui/states";
import { formatLatency, formatMoney } from "@/lib/format";
import { useItemDetail } from "../hooks";
import { InputSection, TraceSection } from "./item-sections";
import { Scores } from "./item-scores";
import { versionLabel } from "../detail/target-line";
import type { ExperimentSummary } from "../types";

export function ItemPanel({
  experiment,
  itemId,
  onClose,
}: {
  experiment: ExperimentSummary;
  itemId: string;
  onClose: () => void;
}) {
  const item = useItemDetail(experiment.id, itemId);
  const d = item.data;
  return (
    <SidePanel
      width={620}
      onClose={onClose}
      title={
        d
          ? `Item ${d.position + 1}${experiment.repetitions > 1 ? ` · repetition ${d.repetition}` : ""}`
          : "Item"
      }
      subtitle={
        d && <Pill tone={d.status === "agent_error" ? "err" : "neutral"}>{d.status.replace("_", " ")}</Pill>
      }
    >
      <QueryState query={item} what="the item">
        {d && (
          <>
            {experiment.evaluators.length > 0 && (
              <Section title="Scores">
                <Scores item={d} experiment={experiment} />
              </Section>
            )}
            <InputSection input={d.input} sourceTraceUrl={d.source_trace_url} />
            <Section title="Expected output">
              <JsonView value={d.expected_output} label="Expected output" />
            </Section>
            <Section title="Output">
              {d.status === "agent_error" ? (
                <p role="alert" className="rounded-lg bg-err-bg px-3.5 py-3 text-[13px] text-err">
                  {d.error_code && <strong>{d.error_code}: </strong>}
                  {d.error_message ?? "The agent failed."}
                  {d.attempts > 1 && ` (${d.attempts} attempts)`}
                </p>
              ) : (
                <JsonView value={d.output} label="Output" />
              )}
            </Section>
            <TraceSection toolCalls={d.tool_calls} context={d.context} />
            <Section
              title="Run"
              aside={
                d.trace_url && (
                  <a className="link text-xs" href={d.trace_url} target="_blank" rel="noreferrer">
                    Open trace ↗
                  </a>
                )
              }
            >
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
                <span>
                  Latency <span className="font-mono">{formatLatency(d.latency_ms)}</span>
                </span>
                <span>
                  Cost <span className="font-mono">{formatMoney(d.agent_cost_usd)}</span>
                </span>
                {d.reported_version && (
                  <span>
                    Answered by <span className="font-mono">{versionLabel(d.reported_version)}</span>
                    {d.version_drift && (
                      <Pill tone="warn" className="ml-1.5">
                        Drift: pinned {versionLabel(experiment.agent_version) ?? "another version"}
                      </Pill>
                    )}
                  </span>
                )}
                <span>
                  Attempts <span className="font-mono">{d.attempts}</span>
                </span>
              </div>
              {d.trace_id && !d.trace_url && <span className="src">trace {d.trace_id}</span>}
            </Section>
          </>
        )}
      </QueryState>
    </SidePanel>
  );
}
